import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { LoadError, errMessage } from "@/components/LoadError";
import { SectieBalk } from "./VandaagParts";
import { LIJST } from "@/components/rust/Rust";

// v21 K5: de server rekent (call_staat); dit scherm tekent alleen.
const CHIP = {
  "te-laat": ["Te laat", "bg-rose-50 text-rose-700 border-rose-200"],
  "niet-gebeld": ["Nog niet gebeld", "bg-zinc-50 text-zinc-600 border-zinc-200"],
  "niet-klaar": ["Gebeld, nog niet klaar", "bg-amber-50 text-amber-800 border-amber-200"],
  "klaar-te-laat": ["Klaar, te laat", "bg-amber-50 text-amber-800 border-amber-200"],
  klaar: ["Klaar", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};
const STIP = { groen: "bg-emerald-500", rood: "bg-rose-500", onvolledig: "bg-amber-400" };
const RESULTAAT = { groen: "groen", rood: "rood", onvolledig: "onvolledig" };
const VB = { volledig: "aangeleverd", onvolledig: "deels aangeleverd", niets: "niets aangeleverd", open: "venster nog open", "nog-niet-open": "venster nog niet open", "geen-account": "geen bedrijfsleider-account" };
const vbTekst = (v) => `${VB[v.status] || v.status}${v.totaal ? ` · ${v.ingevuld}/${v.totaal} antwoorden` : ""}`;
const sleutel = (l) => l.replace(/\s+/g, "-");
const moment = (iso) => new Date(iso).toLocaleString("nl-NL", { timeZone: "Europe/Amsterdam", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const Chip = ({ chip, testid }) => {
  const [tekst, cls] = CHIP[chip] || [chip, ""];
  return <span data-testid={testid} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{tekst}</span>;
};

function Ontbreekt({ r, nieuw }) {
  const o = r.staat.ontbreekt;
  return (
    <div data-testid={`calls-week-detail-${sleutel(r.locatie)}`} className="px-3 pb-3 text-[13px] text-zinc-700 space-y-0.5">
      <p>Cijfers {o.cijfers}/{o.cijfersTotaal} · WOW {o.wow ? "ingevuld" : "nog niet"}{r.staat.resultaat ? ` · resultaat ${RESULTAAT[r.staat.resultaat]}` : ""}</p>
      {r.staat.klaarOp && <p data-testid={`calls-week-klaarop-${sleutel(r.locatie)}`}>Klaar op {moment(r.staat.klaarOp)}{r.staat.grensLabel ? ` · grens ${r.staat.grensLabel}` : ""}</p>}
      {!r.staat.klaarOp && r.staat.grensLabel && <p>Grens {r.staat.grensLabel}</p>}
      {nieuw && <p>Checks beantwoord {o.checksBeantwoord}/{o.checksTotaal} · afgevinkt {o.checksAfgevinkt}/{o.checksTotaal}</p>}
      <p>Voorbereiding: {vbTekst(r.voorbereiding)}</p>
      {r.callDoor && <p className="text-zinc-500">Call door <span className="capitalize">{r.callDoor}</span></p>}
      {r.staat.nogNodig?.length > 0 && <p className="text-zinc-500">Nog nodig voor klaar: {r.staat.nogNodig.join(" · ")}</p>}
    </div>
  );
}

function CallRij({ r, nieuw }) {
  const [open, setOpen] = useState(false);
  const k = sleutel(r.locatie);
  return (
    <li>
      <button type="button" data-testid={`calls-week-rij-${k}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full min-h-[44px] text-left flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px] hover:bg-zinc-50 transition-colors">
        <span className={`h-2 w-2 rounded-full ${STIP[r.staat.resultaat] || "bg-zinc-300"}`} data-testid={`calls-week-stip-${k}`} />
        <span className="font-semibold text-zinc-900 min-w-[110px]">{r.locatie}</span>
        <span className="text-xs text-zinc-500 capitalize">{r.oms.length ? r.oms.join(", ") : "geen OM"}</span>
        <span className="ml-auto flex items-center gap-2"><Chip chip={r.staat.chip} testid={`calls-week-chip-${k}`} />
          {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}</span>
      </button>
      {open && <Ontbreekt r={r} nieuw={nieuw} />}
    </li>
  );
}

function VbRij({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button type="button" data-testid={`calls-vb-rij-${sleutel(r.locatie)}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full min-h-[44px] text-left flex flex-wrap items-center gap-x-3 px-3 py-2 text-[13px] hover:bg-zinc-50 transition-colors">
        <span className="font-semibold text-zinc-900 min-w-[110px]">{r.locatie}</span>
        <span className="ml-auto text-xs text-zinc-500">{VB[r.status] || r.status}</span>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
      </button>
      {open && <p data-testid={`calls-vb-detail-${sleutel(r.locatie)}`} className="px-3 pb-3 text-[13px] text-zinc-700">{vbTekst(r)}</p>}
    </li>
  );
}

const NavKnop = ({ week, onGo, testid, children, label }) => (
  <button type="button" data-testid={testid} aria-label={label} disabled={!week} onClick={() => onGo(week)}
    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md bg-white text-zinc-950 disabled:opacity-30 hover:bg-zinc-200 transition-colors">{children}</button>
);

export function CallsDezeWeek() {
  const [week, setWeek] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const [laden, setLaden] = useState(true);
  useEffect(() => {
    let alive = true;
    setErr(null);
    setLaden(true);
    api.get(`/vandaag/calls${week ? `?week=${week}` : ""}`).then((r) => { if (alive) setD(r.data); })
      .catch((e) => { if (alive) setErr(errMessage(e)); })
      .finally(() => { if (alive) setLaden(false); });
    return () => { alive = false; };
  }, [week, tick]);
  // v21-R1 R7: Opnieuw haalt de week die de gebruiker vroeg (week-state blijft staan); de pijltjes blijven bij een fout.
  const fout = err && <LoadError error={err} onRetry={() => setTick((t) => t + 1)} testid="calls-week-fout" />;
  if (!d) return <div className="mb-3 md:mb-4">{fout || <p data-testid="calls-week-laden" className="text-sm text-zinc-500">Calls laden…</p>}</div>;
  const v = d.voorbereiding;
  const nav = (
    <span className="flex items-center gap-1">
      <NavKnop week={d.vorigeWeek} onGo={setWeek} testid="calls-week-vorige" label="Vorige week"><ChevronLeft className="h-4 w-4" /></NavKnop>
      <NavKnop week={d.volgendeWeek} onGo={setWeek} testid="calls-week-volgende" label="Volgende week"><ChevronRight className="h-4 w-4" /></NavKnop>
    </span>
  );
  return (
    <>
      <div className="mb-3 md:mb-4" data-testid="vandaag-calls-week">
        <SectieBalk testid="vandaag-sectie-calls-week" titel={d.week === d.huidigeWeek ? "Calls deze week" : `Calls ${d.callInfo.callLabel}`} actie={nav} />
        {fout && <div className="mb-2">{fout}</div>}
        {laden && <p data-testid="calls-week-laden" className="text-xs text-zinc-500 mb-1">Week laden…</p>}
        <div className={`bg-white border border-zinc-200 rounded-[10px] transition-opacity ${laden || err ? "opacity-40 pointer-events-none" : ""}`} aria-busy={laden}>
          <p data-testid="calls-week-samenvatting" className="px-3 py-2.5 text-sm font-semibold text-zinc-800 border-b border-zinc-100">{d.samenvattingZin}</p>
          {d.grensLabel && <p data-testid="calls-week-grens" className="px-3 pt-2 text-xs text-zinc-500">Te laat na {d.grensLabel}</p>}
          {!d.nieuweRegels && <p data-testid="calls-week-oude-regels" className="px-3 pt-2 text-xs text-zinc-500">Deze week telt nog de oude betekenis: klaar = cijfers en WOW, checks tellen niet mee.</p>}
          <ul className="divide-y divide-zinc-200 mt-1">{d.locaties.map((r) => <CallRij key={`${d.week}-${r.locatie}`} r={r} nieuw={d.nieuweRegels} />)}</ul>
        </div>
      </div>
      <div className="mb-3 md:mb-4" data-testid="vandaag-voorbereiding-volgende">
        <SectieBalk testid="vandaag-sectie-voorbereiding" titel={`Voorbereiding voor de call van ${v.callInfo.callLabel}`} />
        <div className="bg-white border border-zinc-200 rounded-[10px]">
          <p data-testid="calls-vb-samenvatting" className="px-3 py-2.5 text-sm font-semibold text-zinc-800 border-b border-zinc-100">{v.zin}</p>
          <ul className={`${LIJST} border-0 rounded-none`}>{v.locaties.map((r) => <VbRij key={r.locatie} r={r} />)}</ul>
        </div>
      </div>
    </>
  );
}
